/**
 * Handles custom product pricing in the cart
 * - Recalculates prices for custom products using calculateCustomProductPrice
 * - Updates displayed prices and cart totals
 * - Listens for cart update events
 */

(function() {
  'use strict';

  let customProducts = {};
  let customProductsLoaded = false;
  let cartData = null;
  let recalcScheduled = false;
  let isUpdating = false;

  function afterDOMUpdated(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }

  function scheduleRecalc() {
    if (recalcScheduled || isUpdating) return;
    recalcScheduled = true;
    afterDOMUpdated(() => {
      recalcScheduled = false;
      updateCartPricingWithRetry(0);
    });
  }

  function hasMissingPriceNodes() {
    if (!cartData || !cartData.items) return false;

    let hasCustomItems = false;
    let missingNode = false;

    cartData.items.forEach(item => {
      const variantId = String(item.variant_id);
      if (customProducts[variantId]) {
        hasCustomItems = true;
        const node = document.querySelector(
          `.cart-item-price[data-item-key="${item.key}"]`
        );
        if (!node) missingNode = true;
      }
    });

    if (hasCustomItems) {
      const totalNode = document.querySelector('[ref="cartTotal"]');
      if (!totalNode) missingNode = true;
    }

    return missingNode;
  }

  function updateCartPricingWithRetry(attempt) {
    const maxAttempts = 3;
    updateCartPricing(false).then(() => {
      if (attempt >= maxAttempts) return;
      if (hasMissingPriceNodes()) {
        afterDOMUpdated(() => {
          updateCartPricingWithRetry(attempt + 1);
        });
      }
    });
  }

  function loadInitialCartData() {
    const el = document.getElementById('CartInitialData');
    if (!el) return null;

    try {
      const data = JSON.parse(el.textContent || '{}');
      if (data && data.items) {
        cartData = data;
        return cartData;
      }
    } catch (err) {
      console.error('Failed to parse initial cart JSON:', err);
    }

    return null;
  }

  // Recalculate price for a single cart item
  function recalculateItemPrice(item, productConfig) {
    const inputValues = getInputValuesFromProperties(item.properties);
    const result = calculateCustomProductPrice(productConfig, inputValues);
    
    // Return price in cents for the line (quantity * unit price)
    return result.totalPriceCents * item.quantity;
  }

  function setTextComponentPrice(selector, itemKey, priceCents) {
    const element = document.querySelector(
      `${selector}[data-item-key="${itemKey}"]`
    );
    if (!element) return null;

    const formattedPrice = formatMoney(priceCents);
    const numericValue = formattedPrice.replace(/[^\d.,]/g, '');
    element.setAttribute('value', numericValue);
    element.textContent = formattedPrice;
    return element;
  }

  function setPriceText(selector, itemKey, priceCents) {
    const element = document.querySelector(
      `${selector}[data-item-key="${itemKey}"]`
    );
    if (!element) return;

    element.textContent = formatMoney(priceCents);
  }

  function getDiscountPercent(itemKey) {
    const priceElement = document.querySelector(
      `.cart-item-price[data-item-key="${itemKey}"]`
    );
    const wrapper = priceElement?.closest('[data-cart-discount-percent]');
    const percent = Number(wrapper?.dataset.cartDiscountPercent);
    return Number.isFinite(percent) && percent > 0 ? percent : 0;
  }

  function getOriginalPriceFromDiscount(discountedPriceCents, discountPercent) {
    const multiplier = 100 - discountPercent;
    if (multiplier <= 0) return 0;
    return Math.round(discountedPriceCents * 100 / multiplier);
  }

  // Update displayed unit and line total price for a cart item
  function updateItemPriceDisplay(itemKey, linePriceCents, quantity) {
    setTextComponentPrice('.cart-item-price', itemKey, linePriceCents);
    const safeQty = Number(quantity) > 0 ? Number(quantity) : 1;
    const unitPriceCents = Math.round(linePriceCents / safeQty);
    setTextComponentPrice('.cart-item-unit-price', itemKey, unitPriceCents);

    const discountPercent = getDiscountPercent(itemKey);
    if (discountPercent > 0) {
      setPriceText(
        '.cart-item-price-compare',
        itemKey,
        getOriginalPriceFromDiscount(linePriceCents, discountPercent)
      );
      setPriceText(
        '.cart-item-unit-price-compare',
        itemKey,
        getOriginalPriceFromDiscount(unitPriceCents, discountPercent)
      );
    }
  }

  // Recalculate all custom product prices and update display
  async function recalculateCustomPrices() {
    if (!cartData || !cartData.items) return;

    // Wait for custom products to be loaded if not ready yet
    if (!customProductsLoaded) {
      // Retry after a short delay
      setTimeout(() => {
        if (customProductsLoaded) {
          scheduleRecalc();
        }
      }, 100);
      return;
    }

    let hasCustomItems = false;
    let customItemsTotal = 0;
    let regularItemsTotal = 0;

    // Process each cart item
    cartData.items.forEach(item => {
      const variantId = String(item.variant_id);
      const productConfig = customProducts[variantId];

      if (productConfig) {
        // This is a custom product - recalculate price
        hasCustomItems = true;
        const recalculatedPrice = recalculateItemPrice(item, productConfig);
        customItemsTotal += recalculatedPrice;
        
        // Update displayed price
        updateItemPriceDisplay(item.key, recalculatedPrice, item.quantity);
      } else {
        // Regular product - use original price
        regularItemsTotal += item.final_line_price;
      }
    });

    // Update cart total if we have custom items
    if (hasCustomItems) {
      const newTotal = customItemsTotal + regularItemsTotal;
      updateCartTotal(newTotal);
    }

    return hasCustomItems;
  }

  // Update cart total display
  function updateCartTotal(totalCents) {
    const cartTotalElement = document.querySelector('[ref="cartTotal"]');
    if (!cartTotalElement) return;

    const formattedTotal = formatMoney(totalCents);
    
    // Update text-component value and text content
    cartTotalElement.setAttribute('value', formattedTotal.replace(/[^\d.,]/g, ''));
    cartTotalElement.textContent = formattedTotal;
  }

  // Main function to update cart pricing
  async function updateCartPricing() {
    isUpdating = true;
    if (!cartData || !cartData.items) {
      isUpdating = false;
      return;
    }
    await recalculateCustomPrices();
    
    // Update property labels after pricing is calculated
    if (typeof updateCartPropertyLabels === 'function') {
      afterDOMUpdated(() => {
        updateCartPropertyLabels();
      });
    }
    
    isUpdating = false;
  }

  // Listen for cart update events
  function handleCartUpdate(event) {
    const eventCart = event?.detail?.resource;
    if (eventCart && eventCart.items) {
      cartData = eventCart;
    }
    // Wait for the cart DOM to be morphed, then recalc
    scheduleRecalc();
  }

  // Initialize when DOM is ready
  async function init() {
    // Get preloaded custom products data from shared DOM JSON node
    const customProductsConfigEl = document.querySelector('[data-custom-products-config]');
    
    // Load initial cart data first
    loadInitialCartData();
    
    // Load custom products using preloaded DOM data
    try {
      customProducts = loadCustomProducts(customProductsConfigEl);
      customProductsLoaded = true;
      // Trigger recalculation once custom products are loaded
      scheduleRecalc();
    } catch (err) {
      console.error('Failed to load custom products:', err);
      customProductsLoaded = false;
    }

    // Listen for cart update events (theme uses 'cart:update')
    document.addEventListener('cart:update', handleCartUpdate);

    // Also listen for custom cart:updated event
    window.addEventListener('cart:updated', handleCartUpdate);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
